import { Col, Row } from 'antd';
import { forEach, map, nth, slice } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMedia } from 'react-use';
import useLazyQueryWithCallbacks from '../../../common/CommonUseLazyQuery';
import {
  BREAKPOINTS,
  DATETIMEWITHDIVIDE,
  DEFAULT_PAGE_SIZE,
} from '../../../common/constants';
import useRouter from '../../../common/useRouter';
import { removeHistoryStateData } from '../../../common/utils';
import CommonCard from '../../../components/CommonCard';
import CommonDropdown from '../../../components/CommonDropdown';
import CommonTable from '../../../components/CommonTable';
import CommonTooltip from '../../../components/CommonTooltip';
import EllipsisText from '../../../components/EllipsisText';
import InfiniteScrollHandler from '../../../components/InfiniteScrollHandler';
import Portal from '../../../components/Portal';
import SearchComponent from '../../../components/SearchComponent';
import { GET_PROJECT_DROPDOWN_LIST } from '../../projects/graphql/Queries';
import { GET_USER_REGISTER_APPROVAL_LOGS } from '../graphql/Queries';
import ThankyouModal from './ThankyouModal';

const RegisterTodoList = () => {
  const initialTodoFilter = {
    skip: 0,
    limit: 10,
    sortBy: { field: 'updatedAt', order: 'DESC' },
  };

  const initialPaginationValue = {
    total: 0,
    current: 1,
  };

  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [approvalListData, setApprovalListData] = useState();
  const [sortedInfo, setSortedInfo] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [todoListFilter, setTodoListFilter] = useState(initialTodoFilter);
  const [showModal, setShowModal] = useState(false);
  const { navigate, location } = useRouter();

  const [fetchTodoData, { loading }] = useLazyQueryWithCallbacks(
    GET_USER_REGISTER_APPROVAL_LOGS,
    {
      variables: { filter: todoListFilter },
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const data = res?.getUserRegisterApprovalLogs?.data;
        const pagination = {
          ...paginationProp,
          total: res?.getUserRegisterApprovalLogs?.total,
        };
        if (scrollFlag) {
          const datalist = [...approvalListData, ...data];
          setApprovalListData(datalist);
          setScrollFlag(false);
        } else {
          const datalist = [...data];
          setApprovalListData(datalist);
        }
        setHasMore(!!data?.length);
        setPaginationProp(pagination);
      },
      onError() {},
    },
  );

  useEffect(() => {
    if (location?.state?.showThankyouModal) {
      setShowModal(true);
    }
  }, [location?.state?.showThankyouModal]);

  useEffect(() => {
    fetchTodoData({ variables: { filter: todoListFilter } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (pagination, filterData, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setSortedInfo(sorter);
    setPaginationProp({ ...paginationProp, ...pagination });
    if (sorter?.column) {
      setTodoListFilter({
        ...todoListFilter,
        skip,
        limit: pagination.pageSize,
        sortBy: {
          field: sorter.columnKey,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      });
      fetchTodoData({
        variables: {
          filter: {
            ...todoListFilter,
            skip,
            limit: pagination.pageSize,
            sortBy: {
              field: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
            },
          },
        },
      });
    } else {
      setTodoListFilter({
        ...todoListFilter,
        skip,
        limit: pagination.pageSize,
        sortBy: { field: 'updatedAt', order: 'DESC' },
      });
      fetchTodoData({
        variables: {
          filter: {
            ...todoListFilter,
            skip,
            limit: pagination.pageSize,
            sortBy: { field: 'updatedAt', order: 'DESC' },
          },
        },
      });
    }
  };

  const getApprovalLevel = (levelName) => {
    return (
      <div id="stage-name-status">
        <div id="level-name">{levelName || '-'}</div>
      </div>
    );
  };

  const getApproverName = (values) => {
    const fetchName = [];
    forEach(values, (approver) => {
      const {
        externalUserApprover: extApprName,
        internalUserApprover: intApprName,
      } = approver;
      if (intApprName?.name || extApprName?.name) {
        fetchName.push(intApprName?.name || extApprName?.name);
      }
    });
    const selectedName = nth(fetchName, 0);
    if (fetchName?.length === 1) {
      return (
        <CommonTooltip
          title={selectedName}
          placement={isDesktopViewport ? 'right' : 'top'}
        >
          {selectedName}
        </CommonTooltip>
      );
    }
    if (fetchName?.length > 1) {
      return (
        <CommonTooltip
          title={slice(fetchName, 1, fetchName?.length).join(', ')}
          placement={isDesktopViewport ? 'right' : 'top'}
        >
          {`${selectedName} and +${fetchName?.length - 1}`}
        </CommonTooltip>
      );
    }
    return '-';
  };

  const columns = [
    {
      title: '#',
      key: 'id',
      render: (text, record, index) => {
        return (
          <div id={`register-todo-index-${index}`}>
            {todoListFilter?.skip + index + 1}
          </div>
        );
      },
    },
    {
      title: 'PROJECT',
      dataIndex: 'projectName',
      key: 'projectName',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'projectName' && sortedInfo?.order,
      render: (text, record, index) => (
        <div id={`project-name-${index}`}>
          <EllipsisText text={record?.project?.name} />
        </div>
      ),
    },
    {
      title: 'REGISTER NAME',
      dataIndex: 'registerName',
      key: 'registerName',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'registerName' && sortedInfo?.order,
      render: (text, record, index) => (
        <div id={`register-name-${index}`}>
          <EllipsisText text={record?.register?.name || 'Register'} />
        </div>
      ),
    },
    {
      title: 'Approval Level',
      key: 'levelName',
      dataIndex: 'levelName',
      render: (text) => {
        return getApprovalLevel(text);
      },
    },
    {
      title: 'APPROVERS',
      key: ['registerLog', 'approvers'],
      dataIndex: ['registerLog', 'approvers'],
      render: (values, record) => getApproverName(values, record),
    },
    {
      title: 'REQUESTED BY',
      key: 'creator',
      dataIndex: 'creator',
      render: (text, record) => record?.registerLog?.submitter?.name || '-',
    },
    {
      title: 'REQUESTED ON',
      key: 'createdAt',
      dataIndex: 'createdAt',
      render: (text, record) =>
        record?.registerLog?.submittedForApprovalAt
          ? moment(record?.registerLog?.submittedForApprovalAt)
              ?.tz(record?.project?.timeZone)
              ?.format(DATETIMEWITHDIVIDE)
          : '-',
    },
    {
      title: 'REVIEW',
      key: 'approvalUrl',
      dataIndex: 'approvalUrl',
      render: (_, record, index) => (
        <div className="d-flex align-center" id={`approval-link-${index}`}>
          <Link
            to={`/register-approval/${record?.versionedRegisterId}/${record?.registerLogId}`}
            id={`review-link-${index}`}
          >
            Review
          </Link>
        </div>
      ),
    },
  ];

  const onSearchChange = async (value) => {
    setTodoListFilter({ ...todoListFilter, skip: 0, search: value });
    setPaginationProp(initialPaginationValue);
    fetchTodoData({
      variables: { filter: { ...todoListFilter, skip: 0, search: value } },
    });
  };

  const handleRefetch = () => {
    fetchTodoData({
      variables: {
        filter: {
          ...todoListFilter,
          skip: approvalListData?.length,
          limit: DEFAULT_PAGE_SIZE,
          sortBy: { field: 'createdAt', order: 'DESC' },
        },
      },
    });
  };

  const onProjectChange = (projectId) => {
    setPaginationProp({ ...paginationProp, current: 1 });
    setTodoListFilter({
      ...todoListFilter,
      projectId,
      skip: 0,
    });
    fetchTodoData({
      variables: {
        filter: {
          ...todoListFilter,
          projectId: projectId ? Number(projectId) : undefined,
          skip: 0,
        },
      },
    });
  };

  return (
    <>
      <Portal portalId="search-component">
        <div
          className={isDesktopViewport ? 'd-flex' : 'width-percent-100'}
          id="register-todo-search-portal"
        >
          <Row
            justify={isDesktopViewport ? 'end' : 'start'}
            className="d-flex filter-search"
            wrap
            gutter={isDesktopViewport ? [10, 10] : [0, 10]}
          >
            <Col span={isDesktopViewport ? null : 24}>
              <CommonDropdown
                id="register-project-filter-dropdown"
                allowClear
                placeholder="Project"
                className={`instruction-selector dropdown-width-auto ${
                  isDesktopViewport ? 'width-200' : 'width-percent-100'
                } `}
                onChange={onProjectChange}
                query={GET_PROJECT_DROPDOWN_LIST}
                variables={{ filter: { todo: true } }}
                fetchPolicy="network-only"
                responsePath="projectDropdownList.data"
                valuePath="id"
                labelPath="name"
                optionKey="project"
                showSearch
                optionFilterProp="children"
                dropdownMatchSelectWidth={false}
              />
            </Col>
            <Col span={isDesktopViewport ? null : 24}>
              <SearchComponent
                id="register-search-container-id"
                getData={onSearchChange}
              />
            </Col>
          </Row>
        </div>
      </Portal>
      {showModal && (
        <ThankyouModal
          setShowModal={setShowModal}
          showModal={showModal}
          loading={loading}
          count={paginationProp?.total}
          handleClick={() => {
            removeHistoryStateData(navigate, location, 'showThankyouModal');
          }}
        />
      )}
      {isDesktopViewport ? (
        <CommonTable
          id="register-todo-desktop-table"
          loadingData={loading}
          columns={columns}
          data={approvalListData || []}
          onChange={handleTableChange}
          paginationConfig={paginationProp}
          rowKey={(obj) => obj?.id}
        />
      ) : (
        <InfiniteScrollHandler
          id="register-todo-mobile-scroll"
          scrollFlag={scrollFlag}
          loading={loading}
          refetchData={handleRefetch}
          setScrollFlag={setScrollFlag}
          hasMore={hasMore}
          dataLength={approvalListData?.length}
          skeletonRows={columns?.length - 2}
        >
          {map(approvalListData, (todo, index) => {
            return (
              <CommonCard key={todo?.id} id={`register-todo-card-${index}`}>
                <div
                  className="common-card d-flex"
                  id={`register-todo-card-content-${index}`}
                >
                  <div
                    className="mr-5 fw-medium"
                    id={`register-todo-index-mobile-${index}`}
                  >
                    {index + 1}.
                  </div>
                  <div id={`register-todo-details-${index}`}>
                    <div className="card-header fw-medium">
                      <span>{todo?.project?.name}</span>
                    </div>
                    <div className="card-content text-secondary">
                      <br />
                      <div className="mb-15">
                        <span className="fw-medium mr-5">Register Name:</span>
                        {todo?.register?.name || 'Register'}
                      </div>
                      <div className="d-flex mb-15">
                        <span className="fw-medium mr-5">Approval Level:</span>
                        {getApprovalLevel(todo?.levelName) || '-'}
                      </div>
                      <div className="mb-15">
                        <span className="fw-medium mr-5">Approver:</span>
                        {getApproverName(todo?.registerLog?.approvers, todo)}
                      </div>
                      <div className="mb-15">
                        <span className="fw-medium mr-5">Requested by:</span>
                        {todo?.registerLog?.submitter?.name || '-'}
                      </div>
                      <div className="mb-15">
                        <span className="fw-medium mr-5">Requested on:</span>
                        {todo?.registerLog?.submittedForApprovalAt
                          ? moment(
                              todo?.registerLog?.submittedForApprovalAt,
                            )?.format(DATETIMEWITHDIVIDE)
                          : '-'}
                      </div>
                      <div className="mb-15">
                        <span className="fw-medium mr-5">Review:</span>
                        <span>
                          <Link
                            to={`/register-approval/${todo?.versionedRegisterId}/${todo?.registerLogId}`}
                          >
                            Review
                          </Link>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CommonCard>
            );
          })}
        </InfiniteScrollHandler>
      )}
    </>
  );
};

export default RegisterTodoList;
